import { errorHandler, ErrorType } from '../services/errorHandler';

/**
 * Retry utility for handling retryable operations
 * Requirements: 5.5, 7.5, 10.2 - Retry logic with exponential backoff
 */

export interface RetryOptions {
  maxAttempts?: number;
  baseDelay?: number;
  maxDelay?: number;
  retryCondition?: (error: any) => boolean;
  onRetry?: (error: any, attempt: number) => void;
}

export class RetryError extends Error {
  public readonly attempts: number;
  public readonly lastError: any;

  constructor(message: string, attempts: number, lastError: any) {
    super(message);
    this.name = 'RetryError';
    this.attempts = attempts;
    this.lastError = lastError;
  }
}

/**
 * Retry an async operation with exponential backoff
 */
export async function withRetry<T>(
  operation: () => Promise<T>,
  options: RetryOptions = {}
): Promise<T> {
  const {
    maxAttempts = 3,
    baseDelay = 1000,
    maxDelay = 30000,
    retryCondition = defaultRetryCondition,
    onRetry
  } = options;

  let lastError: any;
  
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error;
      
      // Handle and classify the error
      const appError = errorHandler.handleError(error);
      
      // Check if we should retry
      const shouldRetry = attempt < maxAttempts && 
                         retryCondition(error) && 
                         errorHandler.isRetryable(appError);
      
      if (!shouldRetry) {
        throw error;
      }

      // Calculate delay for next attempt
      const delay = calculateDelay(attempt - 1, baseDelay, maxDelay);
      
      // Notify about retry attempt
      if (onRetry) {
        onRetry(error, attempt);
      }

      console.warn(`Retry attempt ${attempt}/${maxAttempts} after ${delay}ms:`, appError.message);
      
      // Wait before retrying
      await sleep(delay);
    }
  }

  // If we get here, all attempts failed
  throw new RetryError(
    `Operation failed after ${maxAttempts} attempts`,
    maxAttempts,
    lastError
  );
}

/**
 * Default retry condition - retry on network, timeout, and server errors
 */
function defaultRetryCondition(error: any): boolean {
  const appError = errorHandler.handleError(error);
  
  return [
    ErrorType.NETWORK_ERROR,
    ErrorType.TIMEOUT_ERROR,
    ErrorType.SERVER_ERROR
  ].includes(appError.type);
}

/**
 * Calculate delay with exponential backoff and jitter
 */
function calculateDelay(attempt: number, baseDelay: number, maxDelay: number): number {
  // Exponential backoff: baseDelay * 2^attempt
  const exponentialDelay = baseDelay * Math.pow(2, attempt);
  
  // Cap at maxDelay
  const cappedDelay = Math.min(exponentialDelay, maxDelay);
  
  // Add jitter (±25% of the delay)
  const jitter = cappedDelay * 0.25 * (Math.random() - 0.5);
  
  return Math.max(cappedDelay + jitter, 100); // Minimum 100ms
}

/**
 * Sleep utility
 */
function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Retry wrapper for API calls
 */
export function retryApiCall<T>(
  apiCall: () => Promise<T>,
  options: RetryOptions = {}
): Promise<T> {
  return withRetry(apiCall, {
    maxAttempts: 3,
    baseDelay: 1000,
    maxDelay: 10000,
    retryCondition: (error) => {
      const appError = errorHandler.handleError(error);
      return [
        ErrorType.NETWORK_ERROR,
        ErrorType.TIMEOUT_ERROR,
        ErrorType.SERVER_ERROR
      ].includes(appError.type);
    },
    ...options
  });
}

/**
 * Retry wrapper for GraphQL operations
 */
export function retryGraphQLOperation<T>(
  operation: () => Promise<T>,
  options: RetryOptions = {}
): Promise<T> {
  return withRetry(operation, {
    maxAttempts: 2, // GraphQL operations typically need fewer retries
    baseDelay: 500,
    maxDelay: 5000,
    retryCondition: (error) => {
      // Retry on network errors and server errors, but not on GraphQL validation errors
      if (error.graphQLErrors && error.graphQLErrors.length > 0) {
        const graphQLError = error.graphQLErrors[0];
        const code = graphQLError.extensions?.code;
        
        // Don't retry on client errors
        if (['BAD_USER_INPUT', 'UNAUTHENTICATED', 'FORBIDDEN'].includes(code)) {
          return false;
        }
      }
      
      const appError = errorHandler.handleError(error);
      return [
        ErrorType.NETWORK_ERROR,
        ErrorType.TIMEOUT_ERROR,
        ErrorType.SERVER_ERROR
      ].includes(appError.type);
    },
    ...options
  });
}

/**
 * Circuit breaker pattern for preventing cascading failures
 */
export class CircuitBreaker {
  private failures = 0;
  private lastFailureTime = 0;
  private state: 'CLOSED' | 'OPEN' | 'HALF_OPEN' = 'CLOSED';

  constructor(
    private readonly failureThreshold = 5,
    private readonly recoveryTimeout = 60000 // 1 minute
  ) {}

  async execute<T>(operation: () => Promise<T>): Promise<T> {
    if (this.state === 'OPEN') {
      if (Date.now() - this.lastFailureTime > this.recoveryTimeout) {
        this.state = 'HALF_OPEN';
      } else {
        throw new Error('Circuit breaker is OPEN');
      }
    }

    try {
      const result = await operation();
      this.onSuccess();
      return result;
    } catch (error) {
      this.onFailure();
      throw error;
    }
  }

  private onSuccess(): void {
    this.failures = 0;
    this.state = 'CLOSED';
  }

  private onFailure(): void {
    this.failures++;
    this.lastFailureTime = Date.now();

    if (this.failures >= this.failureThreshold) {
      this.state = 'OPEN';
    }
  }

  getState(): string {
    return this.state;
  }

  reset(): void {
    this.failures = 0;
    this.lastFailureTime = 0;
    this.state = 'CLOSED';
  }
}